import React from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Card,
    CardBody,
    CardHeader,
    Typography,
} from "@material-tailwind/react";
import { useSelector } from "react-redux";
import { userSelector } from "store/user";
import useSWR from "swr";
import { fetcherSimple } from "utils/api";
import { useNavigate } from "react-router";
import { STRIPE_PORTAL_LINK } from "utils/config";

const SubscriptionDialog = ({ open, onClose }) => {
    const handleClose = () => {
        onClose();
    };
    const navigate = useNavigate();
    const user = useSelector(userSelector);

    const { data: productsData } = useSWR(
        [`/v2/stripe_item?stripe_only=1`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const recurringProducts = productsData?.filter(product => product.type === 'recurring') || [];

    const handlePurchase = (sku) => {
        navigate(`/payment?product_sku=${sku}`);
        onClose();
    };

    const handleCustomerPortal = () => {
        const email = user.userData?.email;
        const fullUrl = STRIPE_PORTAL_LINK + (email ? `?prefilled_email=${encodeURIComponent(email)}` : "");
        window.open(fullUrl, "_blank");
    };

    const formatPrice = (price) => {
        return price > 1
            ? Intl.NumberFormat("en-EN", {
                style: "currency",
                currency: "GBP",
                trailingZeroDisplay: "stripIfInteger",
            }).format(price / 100)
            : "Free";
    };

    return (
        <Dialog open={open} handler={handleClose} className="dialog">
            <DialogHeader className="dialog__header">
                Choose Your Plan
            </DialogHeader>
            <DialogBody className="dialog__body">
                {!user.activeSubscription && !user.isManaged && (
                    <Typography variant="small" className="text-center block -mt-2 mb-4 text-primary-text font-body">
                        Create teams and collaborate on surveys
                    </Typography>
                )}
                {user.isManaged ? (
                    <div className="text-center">
                        <Typography variant="h5" className="mb-4">
                            You have an active Managed Account
                        </Typography>
                        <Typography variant="paragraph" className="mb-6">
                            Your plan and seats are managed directly by your Gobby representative. Please contact support to make changes to your plan.
                        </Typography>
                    </div>
                ) : user.activeSubscription ? (
                    <div className="text-center">
                        <Typography variant="h5" className="mb-4">
                            You have an active subscription
                        </Typography>
                        <Typography variant="paragraph" className="mb-6">
                            Manage your subscription, billing details, and payment methods through the Stripe Customer Portal.
                        </Typography>
                        <Button
                            className="btn btn--primary"
                            onClick={handleCustomerPortal}
                        >
                            Access Customer Portal
                        </Button>
                    </div>
                ) : (
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {recurringProducts.map((product) => {
                                const isMonthly = product.description.toLowerCase().includes('monthly');
                                return (
                                    <div key={product.sku} className="relative overflow-hidden rounded-xl h-full">
                                        <span className={`corner-sash${isMonthly ? ' corner-sash--popular' : ''}`}>
                                            {isMonthly ? 'Popular' : 'Save 25%'}
                                        </span>
                                        <Card className="card h-full">
                                            <CardHeader className="card-inner-header text-center m-0 pb-1 pt-8" floated={false} shadow={false}>
                                                <Typography variant="h5" className="card-header-text">
                                                    {product.short_name} Subscription
                                                </Typography>
                                            </CardHeader>
                                            <CardBody className="card-inner-body text-center p-0">
                                                <Typography variant="h3" className="mb-2">
                                                    {formatPrice(product.price)}
                                                </Typography>
                                                <Typography variant="small" className="mb-4">
                                                per {isMonthly ? 'month' : 'year'} / seat
                                                </Typography>
                                                <Button
                                                    className="btn btn--cta w-full"
                                                    onClick={() => handlePurchase(product.sku)}
                                                >
                                                Subscribe Now
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    </div>
                                ); })}
                        </div>
                    </div>
                )}
            </DialogBody>
            <DialogFooter className="dialog__footer">
                <Button
                    type="button"
                    className="btn btn--primary"
                    variant="filled"
                    onClick={handleClose}
                >
                    Close
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default SubscriptionDialog;
